import React from 'react';
import Input from '@components/Input';
import Button from '@components/Button';
import Radio from '@components/Radio';
import { ReactComponent as Close } from 'img/btn-close.svg';
import './CArmAnglesModal.scss';
import BaseContextMenu from '@components/ContextMenu/BaseContextMenu';

interface Props  {
  initialPrimary?: string;
  initialSecondary?: string;
  onSubmit: (values: { primary: string; secondary: string; roll?: string }) => void;
  onClose: () => void;
};

type State = {
  primaryDir: 'LAO' | 'RAO';
  primaryValue: string;
  secondaryDir: 'CRA' | 'CAU';
  secondaryValue: string;
};

class CArmAnglesModalContent extends React.Component<Props, State> {
  private static parseAngle(input?: string, fallbackDir?: string) {
    if (!input) {
      return {
        dir: (fallbackDir ?? '') as string,
        value: '',
      };
    }
    const text = input.toUpperCase().replace('ROT', '').replace(':', ' ').trim();
    const parts = text.split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
      return { dir: (fallbackDir ?? '') as string, value: '' };
    }
    if (parts.length === 1) {
      if (Number.isFinite(Number(parts[0]))) {
        return { dir: (fallbackDir ?? '') as string, value: parts[0] };
      }
      return { dir: parts[0], value: '' };
    }
    return { dir: parts[0], value: parts[1] };
  }

  state: State = {
    primaryDir: (CArmAnglesModalContent.parseAngle(
      this.props.initialPrimary,
      'RAO',
    ).dir || 'RAO') as 'LAO' | 'RAO',
    primaryValue:
      CArmAnglesModalContent.parseAngle(this.props.initialPrimary, 'RAO').value,
    secondaryDir: (CArmAnglesModalContent.parseAngle(
      this.props.initialSecondary,
      'CRA',
    ).dir || 'CRA') as 'CRA' | 'CAU',
    secondaryValue:
      CArmAnglesModalContent.parseAngle(this.props.initialSecondary, 'CRA').value,
  };

  handleChange =
    (key: keyof State) => (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;

      if (key === 'primaryValue' || key === 'secondaryValue') {
        value = value.replace(/[^0-9.]/g, '');

        const firstDotIndex = value.indexOf('.');
        if (firstDotIndex !== -1) {
          const beforeDot = value.slice(0, firstDotIndex + 1);
          const afterDot = value.slice(firstDotIndex + 1).replace(/\./g, '');
          value = beforeDot + afterDot;
        }

        const [intPart, decimalPart] = value.split('.');
        if (decimalPart && decimalPart.length > 1) {
          value = `${intPart}.${decimalPart.slice(0, 1)}`;
        }

        const isTrailingDot = value.endsWith('.');
        if (value !== '' && value !== '.' && !isTrailingDot) {
          const num = parseFloat(value);
          if (!Number.isNaN(num)) {
            const max = key === 'primaryValue' ? 180 : 90;
            value = String(Math.min(num, max));
          }
        }
      }

      this.setState({ [key]: value } as Pick<State, keyof State>);
    };

  handleSelect =
    (key: keyof State) => (value: string | number) => {
      this.setState({ [key]: value } as Pick<State, keyof State>);
    };

  handleSubmit = () => {
    const {
      primaryDir,
      primaryValue,
      secondaryDir,
      secondaryValue,
    } = this.state;
    if (!primaryValue.trim() || !secondaryValue.trim()) {
      return;
    }
    this.props.onSubmit({
      primary: `${primaryDir}: ${primaryValue.trim()}`,
      secondary: `${secondaryDir}: ${secondaryValue.trim()}`,
    });
  };

  render() {
    const { onClose } = this.props;
    const {
      primaryDir,
      primaryValue,
      secondaryDir,
      secondaryValue,
    } = this.state;

    return (
      <div className="controller">
        <div className="carm-angles-modal">
          <div className="carm-angles-modal-header">
            <div className="carm-angles-modal-title">C-Arm Angles</div>
            <div className="carm-angles-modal-actions">
              <Button color="cardio" onClick={this.handleSubmit}>
                Apply
              </Button>
            </div>
            {/* <Close className="carm-angles-modal-close" onClick={onClose} /> */}
          </div>
          <div className="dividerContent">
            <div className="divider" />
          </div>

          <div className="carm-angles-modal-body">
            <div className="carm-angles-modal-row">
              <div className="carm-angles-modal-label">Primary (LAO/RAO)</div>
              <div className="carm-angles-modal-group">
                <div className="carm-angles-modal-radio-group">
                  <div
                    className="carm-angles-modal-radio"
                    onClick={() => this.handleSelect('primaryDir')('RAO')}
                  >
                    <Radio
                      onChange={() => this.handleSelect('primaryDir')('RAO')}
                      value="RAO"
                      checked={primaryDir === 'RAO'}
                    />
                    <span className="carm-angles-modal-radio-text">RAO</span>
                  </div>
                  <div
                    className="carm-angles-modal-radio"
                    onClick={() => this.handleSelect('primaryDir')('LAO')}
                  >
                    <Radio
                      onChange={() => this.handleSelect('primaryDir')('LAO')}
                      value="LAO"
                      checked={primaryDir === 'LAO'}
                    />
                    <span className="carm-angles-modal-radio-text">LAO</span>
                  </div>
                </div>
                <Input
                  placeholder="0"
                  value={primaryValue}
                  onChange={this.handleChange('primaryValue')}
                  className="defaulInput"
                />
              </div>
            </div>
            <div className="carm-angles-modal-row">
              <div className="carm-angles-modal-label">Secondary (CRA/CAU)</div>
              <div className="carm-angles-modal-group">
                <div className="carm-angles-modal-radio-group">
                  <div
                    className="carm-angles-modal-radio"
                    onClick={() => this.handleSelect('secondaryDir')('CRA')}
                  >
                    <Radio
                      onChange={() => this.handleSelect('secondaryDir')('CRA')}
                      value="CRA"
                      checked={secondaryDir === 'CRA'}
                    />
                    <span className="carm-angles-modal-radio-text">CRA</span>
                  </div>
                  <div
                    className="carm-angles-modal-radio"
                    onClick={() => this.handleSelect('secondaryDir')('CAU')}
                  >
                    <Radio
                      onChange={() => this.handleSelect('secondaryDir')('CAU')}
                      value="CAU"
                      checked={secondaryDir === 'CAU'}
                    />
                    <span className="carm-angles-modal-radio-text">CAU</span>
                  </div>
                </div>
                <Input
                  placeholder="0"
                  value={secondaryValue}
                  onChange={this.handleChange('secondaryValue')}
                  className="defaulInput"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}


export default function CArmAnglesModal(props: Props) {
  return (
    <BaseContextMenu onClose={props.onClose}>
      <CArmAnglesModalContent {...props} />
    </BaseContextMenu>
  );
}
